import queryString from "query-string"
import { ToolType } from "store/apis/toolsAPI/values.ts"
import { userEndpoints } from "store/apis/userAPI/user.api"
import { missionsApi } from "store/apis/missionsAPI/missions.api"
import { PROFESSION_TYPES } from "store/apis/professionsAPI/values"
import { balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import {
  IToolSlotUpgradeSteps,
  IUserStatistics
} from "store/apis/userAPI/user.types"
import { IPaginationReq, IToolFilters } from "utils/interfaces"
import { TAGS, api } from "../../api"
import {
  IBreedToolsResponse,
  IEquipToolsResponse,
  IToolInstance,
  IUserActiveToolDetails,
  IUserToolsResponse
} from "./types"

export const toolsAPI = api.enhanceEndpoints({
  addTagTypes: [
    TAGS.TOOLS,
    TAGS.PROFESSIONS,
    TAGS.PREFORMS,
    TAGS.RUNES,
    TAGS.USER,
    TAGS.MISSIONS
  ]
})

export const toolsEndpoints = toolsAPI.injectEndpoints({
  endpoints: (builder) => ({
    equipTool: builder.mutation<IEquipToolsResponse, string>({
      query: (toolToEquipId) => ({
        url: `/tools/active/${toolToEquipId}`,
        method: "PATCH"
      }),
      extraOptions: { maxRetries: 1 }
    }),
    getAllTools: builder.query<IUserToolsResponse, IPaginationReq>({
      query: ({ page, limit, ...filters }) => {
        return {
          url: `/tools/tools-instances?${queryString.stringify({ page, limit, ...filters }, { skipNull: false, skipEmptyString: true })}`,
          method: "GET"
        }
      },
      providesTags: (
        result,
        _,
        params
      ): readonly { type: typeof TAGS.TOOLS; id: string }[] =>
        result
          ? [
              {
                type: TAGS.TOOLS as const,
                id: `tools-list:page:${params.page}:limit:${params.limit}`
              },
              { type: TAGS.TOOLS as const, id: "USER_TOOL_LIST" }
            ]
          : [{ type: TAGS.TOOLS as const, id: "USER_TOOL_LIST" }]
    }),
    getFilteredTools: builder.query<
      IUserToolsResponse,
      IPaginationReq & IToolFilters
    >({
      query: ({ page, limit, ...filters }) => ({
        url: `/tools/tools-instances?${queryString.stringify({ page, limit, ...filters }, { skipNull: false, skipEmptyString: true })}`,
        method: "GET"
      }),
      providesTags: (
        result,
        _,
        params
      ): readonly { type: typeof TAGS.TOOLS; id: string }[] =>
        result
          ? [
              {
                type: TAGS.TOOLS as const,
                id: `tools-list:page:${params.page}:limit:${params.limit}`
              },
              { type: TAGS.TOOLS as const, id: "FILTERED_USER_TOOL_LIST" }
            ]
          : [{ type: TAGS.TOOLS as const, id: "FILTERED_USER_TOOL_LIST" }]
    }),
    getActiveTool: builder.query<IToolInstance, void>({
      query: () => ({
        url: "/tools/active",
        method: "GET"
      }),
      providesTags: (res) => [{ type: TAGS.TOOLS, id: res?._id }]
    }),
    getActiveToolDetails: builder.query<
      IUserActiveToolDetails,
      { uId: string }
    >({
      query: ({ uId }) => ({
        url: `/tools/active-tool-details/${uId}`,
        method: "GET"
      }),
      providesTags: (res) => [
        { type: TAGS.TOOLS, id: `activeToolDetails_${res?.activeTool._id}` }
      ]
    }),
    getToolById: builder.query<IToolSlotUpgradeSteps, string>({
      query: (id) => ({
        url: `/tools/${id}`,
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: (res) => [{ type: TAGS.TOOLS, id: res?._id }]
    }),
    getUpgradeSteps: builder.query<IToolSlotUpgradeSteps, void>({
      query: () => ({
        url: `/tools/upgrade-tool-slot-steps`,
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: () => [{ type: TAGS.TOOLS, id: "upgrade-tool-slot-steps" }]
    }),
    buyTool: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/tools/buy`,
        method: "POST",
        data: { id }
      }),
      invalidatesTags: [
        { type: TAGS.TOOLS, id: "LIST" },
        { type: TAGS.TOOLS, id: "USER TOOL TYPES COUNT" }
      ]
    }),
    putRunesIntoTool: builder.mutation<
      IEquipToolsResponse,
      { toolId: string; runesIds: string[] }
    >({
      query: ({ runesIds, toolId }) => ({
        url: `/tools/add-runes/${toolId}`,
        method: "PATCH",
        data: { runesIds }
      }),
      extraOptions: { maxRetries: 2 },
      invalidatesTags: () => [{ type: TAGS.RUNES }, { type: TAGS.TOOLS }],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )
      }
    }),
    breedTools: builder.mutation<
      IBreedToolsResponse,
      { toolsToBreed: string[] }
    >({
      query: ({ toolsToBreed }) => ({
        url: `/tools/fuse`,
        method: "PATCH",
        data: { toolsToBreed }
      }),
      extraOptions: { maxRetries: 2 },
      invalidatesTags: () => [
        { type: TAGS.PROFESSIONS, id: PROFESSION_TYPES.SCOUT },
        { type: TAGS.TOOLS },
        { type: TAGS.PREFORMS },
        { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          userEndpoints.util.updateQueryData(
            "getUserGameStatistics",
            undefined,
            (draft: IUserStatistics) => {
              draft.totalToolsCrafted += 1
            }
          )
        )

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )

        dispatch(
          missionsApi.util.invalidateTags([
            { type: TAGS.MISSIONS, id: "MY_PROGRESS" },
            { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
          ])
        )
      }
    }),
    getUserToolTypesCount: builder.query<Record<ToolType, number>, void>({
      query: () => ({
        url: `/tools/count`,
        method: "GET"
      }),
      providesTags: [{ type: TAGS.TOOLS, id: `USER TOOL TYPES COUNT` }]
    })
  })
})
